// src/domains/home.ts —— 06 A-02 概览原生域：KPI 4 卡 / 推广链接 / 我的收益卡（0045：本月开卡差价 / 充值差价 / 汇率差三格 + 账户行 + 我的售价入口）/ 近 14 天柱图（TD-824 / TD-825：migrated 后键退出 parity）
// 跨域契约：HomeSlice / homeOf / loadHome / loadOverview / loadActiveSubs 由 shell、table、subs、withdraw 引用，签名保持稳定
import type { AdminPage } from '../../../shared/src/api/types';
import { interpolate } from '../../../shared/src/errors';
import { fmtCommission, parseDecimalInput, toMicro, type Micro } from '../../../shared/src/money';
import { tzOffsetLabel } from '../../../shared/src/time';
import { I18N_AGENT_HOME, REWRITES_AGENT_HOME } from '../i18n/home';
import type { AgentState } from '../state';
import type { AgentLinksResp, AgentOverviewResp, SubAgentRow } from '../types';
import { KEYS, MODAL_KEYS } from './home.keys';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';

export interface AgentFXView {
  base_rate: string;
  limit_pct: string;
  band: { min: string; max: string };
  current: { rate: string | null; effective_at: string | null; source: 'inherit' | 'agent' | 'out_of_band' };
}
interface FXHistoryRow { id: number; rate: string | null; prev_rate: string | null; effective_at: string; actor_kind: string }
interface FXHistory { list: FXHistoryRow[]; total: number; page: number; size: number }

export interface HomeSlice {
  overview: AgentOverviewResp | null;
  links: AgentLinksResp | null;
  activeSubs: number;
  fxOpen: boolean;
  fx: AgentFXView | null;
  fxHistory: FXHistory | null;
  fxDraft: string;
  fxError: string;
}

declare module '../state' {
  interface AgentSlices {
    ho?: HomeSlice;
  }
}

const HOME0: HomeSlice = { overview: null, links: null, activeSubs: 0, fxOpen: false, fx: null, fxHistory: null, fxDraft: '', fxError: '' };

export function homeOf(s: AgentState): HomeSlice {
  return s.ho ?? HOME0;
}

function setHo(ctx: AgentCtx, patch: Partial<HomeSlice>): void {
  ctx.host.setState({ ho: { ...homeOf(ctx.host.state), ...patch } });
}

async function loadFX(ctx: AgentCtx, page = 1): Promise<void> {
  const epoch = ctx.gp.session.epoch();
  const [view, history] = await Promise.allSettled([
    ctx.gp.api.call<AgentFXView>('GET /api/agent/fx-rate'),
    ctx.gp.api.call<FXHistory>('GET /api/agent/fx-rate/history', { query: { page, size: 20 } }),
  ]);
  if (ctx.gp.session.epoch() !== epoch) return;
  const patch: Partial<HomeSlice> = {};
  if (view.status === 'fulfilled') {
    patch.fx = view.value;
    patch.fxDraft = view.value.current.rate ?? '';
  }
  if (history.status === 'fulfilled') patch.fxHistory = history.value;
  setHo(ctx, patch);
  const failure = view.status === 'rejected' ? view.reason : history.status === 'rejected' ? history.reason : null;
  if (failure) ctx.gp.ui.toast(ctx.gp.ui.err(failure));
}

async function loadMoreFXHistory(ctx: AgentCtx): Promise<void> {
  const current = homeOf(ctx.host.state).fxHistory;
  if (!current || current.page * current.size >= current.total) return;
  const epoch = ctx.gp.session.epoch();
  try {
    const next = await ctx.gp.api.call<FXHistory>('GET /api/agent/fx-rate/history', { query: { page: current.page + 1, size: current.size } });
    if (ctx.gp.session.epoch() !== epoch || homeOf(ctx.host.state).fxHistory !== current) return;
    setHo(ctx, { fxHistory: { ...next, list: [...current.list, ...next.list] } });
  } catch (e) {
    if (ctx.gp.session.epoch() === epoch) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

async function saveFX(ctx: AgentCtx): Promise<void> {
  const ho = homeOf(ctx.host.state);
  if (!ho.fx) return;
  const rate = ho.fxDraft.trim();
  const parsed = rate ? parseDecimalInput(rate, 6) : null;
  if (rate && (parsed === null || toMicro(parsed) < toMicro(ho.fx.band.min) || toMicro(parsed) > toMicro(ho.fx.band.max))) {
    setHo(ctx, { fxError: str(ctx.t, 'fxInvalid') });
    return;
  }
  const epoch = ctx.gp.session.epoch();
  await ctx.gp.ui.run(async () => {
    try {
      const fx = await ctx.gp.api.call<AgentFXView>('PUT /api/agent/fx-rate', { body: { rate: rate || null } });
      if (ctx.gp.session.epoch() !== epoch) return;
      setHo(ctx, { fx, fxDraft: fx.current.rate ?? '', fxError: '' });
      const fxHistory = await ctx.gp.api.call<FXHistory>('GET /api/agent/fx-rate/history', { query: { page: 1, size: 20 } });
      if (ctx.gp.session.epoch() === epoch) setHo(ctx, { fxHistory });
    } catch (e) {
      if (ctx.gp.session.epoch() === epoch) setHo(ctx, { fxError: ctx.gp.ui.err(e) });
    }
  });
}

// 06 A-02：overview 与 links 并行拉取，任一失败只 toast 一次，成功的一半照常落地
// 在途丢弃：发请求时的会话代与写入时不一致（期间退出 / 过期 / 改密 / 换账号）即丢弃结果，防跨账号数据串入（推荐码归属有资金影响）
export async function loadHome(ctx: AgentCtx): Promise<void> {
  const ep = ctx.gp.session.epoch();
  const [ov, ln] = await Promise.allSettled([
    ctx.gp.api.call<AgentOverviewResp>('GET /api/agent/overview'),
    ctx.gp.api.call<AgentLinksResp>('GET /api/agent/links'),
  ]);
  if (ctx.gp.session.epoch() !== ep) return;
  const patch: Partial<HomeSlice> = {};
  if (ov.status === 'fulfilled') patch.overview = ov.value;
  if (ln.status === 'fulfilled') patch.links = ln.value;
  setHo(ctx, patch);
  const failed: unknown = ov.status === 'rejected' ? ov.reason : ln.status === 'rejected' ? ln.reason : null;
  if (failed !== null) ctx.gp.ui.toast(ctx.gp.ui.err(failed));
}

// 只刷 overview（可提现 / 本月佣金 / 本月差价）：佣金页进入、提佣成功后各一次（可提现属资金类展示，不得停留过期值）
export async function loadOverview(ctx: AgentCtx): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const ov = await ctx.gp.api.call<AgentOverviewResp>('GET /api/agent/overview');
    if (ctx.gp.session.epoch() !== ep) return;
    setHo(ctx, { overview: ov });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

// 06 A-02 全局框架：导航「下级分销商」徽标 = 启用中直接下级数（列表 total）；下级写操作成功后复用
export async function loadActiveSubs(ctx: AgentCtx): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const p = await ctx.gp.api.call<AdminPage<SubAgentRow>>('GET /api/agent/sub-agents', { query: { page: 1, size: 10, status: 'active' } });
    if (ctx.gp.session.epoch() !== ep) return;
    setHo(ctx, { activeSubs: p.total });
  } catch (e) {
    console.warn('[gp:agent] 徽标计数失败', e);
  }
}

const DASH = '—';
const KPI_DARK = 'border-radius:16px;padding:16px 18px;background:#0f1a17;color:#fff';
const KPI_LIGHT = 'border-radius:16px;padding:16px 18px;background:#fff;border:1px solid #e6ebe8';
const MUTED = 'color:#9aa5a1;font-weight:600';
// 0057 原型复制按钮样式原串；链接为 null 时追加置灰（06 A-02：链接位「—」、复制按钮置灰）
const COPY_BTN = 'height:32px;padding:0 12px;border:0;border-radius:8px;background:#0d9a68;color:#fff;font-size:12px;font-weight:700;cursor:pointer;flex:none';
const COPY_BTN_OFF = COPY_BTN + ';opacity:.4;cursor:not-allowed';

// 金额 / 费率串按后端 DECIMAL 文本解析（TD-814 BigInt 微单位）；非法值不让整页渲染抛错，降级为「—」并留日志
function microOf(v: string): Micro | null {
  try {
    return toMicro(v);
  } catch (e) {
    console.warn('[gp:agent] 非法金额 / 费率', v, e);
    return null;
  }
}

// 佣金金额（KPI / 本月差价三格 / 柱状图）：「{金额} USDT」去尾零（对客评审 2026-10-15 #26：佣金为 USDT，卡相关才用 $）
function usdt(v: string | null | undefined): string {
  if (v === null || v === undefined) return DASH;
  const m = microOf(v);
  return m === null ? DASH : fmtCommission(m);
}

// 归一化高度：round(v / max × 100) HALF_UP，BigInt 运算避免浮点（max ≤ 0 时全 0；负值按 0）
function heightPct(v: Micro, maxMicro: Micro): number {
  if (maxMicro <= 0n || v <= 0n) return 0;
  return Number((v * 200n + maxMicro) / (2n * maxMicro));
}

// series.date 是后端按 config.timezone 日界切好的日历日（YYYY-MM-DD），直接取 M/D；
// 不经 Date 再按时区格式化，避免 UTC 零点在西半球时区被换算成前一日（TD-815）
function monthDay(date: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(date);
  return m && m[1] !== undefined && m[2] !== undefined ? `${Number(m[1])}/${Number(m[2])}` : DASH;
}

export interface AcctRow {
  label: string;
  value: string;
  valueStyle: string;
}

// 06 A-02 我的收益卡账户行（0045：原「开卡返佣 / 充值返佣 / 已分配给下级 / 下级可设上限」四行删除）：上级分销商 / 平台钱包绑定（R-08）
function acctRows(t: Readonly<Record<string, unknown>>, ov: AgentOverviewResp | null): AcctRow[] {
  if (!ov) return [];
  const bound = ov.wallet_bound_user;
  return [
    { label: str(t, 'upline'), value: ov.upline ? `${ov.upline.name} · ${ov.upline.biz_no}` : str(t, 'uplinePlatform'), valueStyle: '' },
    { label: str(t, 'walletBound'), value: bound ? `${bound.uid} · ${bound.masked_email}` : str(t, 'walletUnbound'), valueStyle: bound ? '' : MUTED },
  ];
}

// 0045 本月收益三格：kpi.month_open / month_topup / month_fx（只计 accrual，三项之和 = 本月佣金）；未拉到前「—」
function earnTiles(t: Readonly<Record<string, unknown>>, ov: AgentOverviewResp | null): Array<{ label: string; value: string }> {
  const k = ov?.kpi;
  return [
    { label: str(t, 'kMonthOpen'), value: usdt(k?.month_open) },
    { label: str(t, 'kMonthTopup'), value: usdt(k?.month_topup) },
    { label: str(t, 'kMonthFx'), value: usdt(k?.month_fx) },
  ];
}

export const home: AgentDomain = {
  name: 'home',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_AGENT_HOME,
  rewrites: REWRITES_AGENT_HOME,
  state: () => ({ ho: { ...HOME0 } }),
  mount(ctx) {
    // 业务请求只在 session:ready / screen 之后发（受限窗口 S-72 只放行 /me 与 /password）
    // 会话结束（退出 / 过期）即复位切片：下一账号登录后、首屏数据返回前不得显示上一账号的推荐码 / 收益 / 徽标
    ctx.events.on('session:lost', () => ctx.host.setState({ ho: { ...HOME0 } }));
    ctx.events.on('session:ready', () => {
      void loadHome(ctx);
      void loadActiveSubs(ctx);
    });
    ctx.events.on('screen', ({ to }) => {
      if (to === 'home') void loadHome(ctx);
    });
  },
  vals(ctx) {
    const { t, s } = ctx;
    const { overview: ov, links: ln } = homeOf(s);
    const ho = homeOf(s);
    const k = ov?.kpi;
    const series = (ov?.series ?? []).map((p) => ({ date: p.date, micro: microOf(p.amount) ?? 0n }));
    const maxMicro = series.reduce<Micro>((acc, p) => (p.micro > acc ? p.micro : acc), 0n);
    const cfg = s.sh.config;
    const first = series[0];
    const last = series[series.length - 1];
    // 06 A-02：links.* 为 null（siteUrl / appUrl 未配置或无可用 Bot）→ 显示「—」、复制按钮置灰且不触发
    const linkRow = (labelKey: string, url: string | null | undefined) => ({
      label: str(t, labelKey),
      url: url ? url : DASH,
      btnStyle: url ? COPY_BTN : COPY_BTN_OFF,
      copy: () => {
        if (url) ctx.gp.ui.copy(url);
      },
    });
    return {
      kpis: [
        { label: str(t, 'kAvailable'), value: usdt(k?.withdrawable), sub: str(t, 'withdraw'), style: KPI_DARK },
        { label: str(t, 'kMonth'), value: usdt(k?.month_commission), sub: str(t, 'dailyCommission'), style: KPI_LIGHT },
        // S-66：子树用户总数，副文本另列直推数
        { label: str(t, 'kUsers'), value: k ? String(k.users_total) : DASH, sub: `${str(t, 'users')} · ${interpolate(str(t, 'kpiDirect'), { n: k?.users_direct ?? 0 })}`, style: KPI_LIGHT },
        { label: str(t, 'kSubs'), value: k ? String(k.sub_agents) : DASH, sub: str(t, 'subs'), style: KPI_LIGHT },
      ],
      links: [linkRow('linkTg', ln?.links.bot), linkRow('linkWeb', ln?.links.web), linkRow('linkApp', ln?.links.app)],
      chart: series.map((p) => ({ v: fmtCommission(p.micro), style: `flex:1;height:${heightPct(p.micro, maxMicro)}%;background:#0d9a68;border-radius:4px 4px 0 0;min-width:6px` })),
      chartStart: first ? monthDay(first.date) : '',
      chartEnd: last ? monthDay(last.date) : '',
      earnTiles: earnTiles(t, ov),
      acctRows: acctRows(t, ov),
      goPrices: () => ctx.host.setState({ page: 'prices', modal: null }),
      fxOpen: ho.fxOpen,
      toggleFX: () => { setHo(ctx, { fxOpen: !ho.fxOpen, fxError: '' }); if (!ho.fxOpen) void loadFX(ctx); },
      // 用户拍板 2026-10-15：兑换汇率 = 1 USDT 入账多少 USDT 余额（钱包是 USDT），不再写 USD/USDT
      fxCurrentText: ho.fx ? `1 USDT → ${ho.fx.current.rate ?? ho.fx.base_rate} USDT · ${str(t, ho.fx.current.source === 'agent' ? 'fxAgent' : ho.fx.current.source === 'out_of_band' ? 'fxFallback' : 'fxInherited')}` : '—',
      fxBandText: ho.fx ? `1 USDT → ${ho.fx.band.min} – ${ho.fx.band.max} USDT` : '—',
      fxBaseText: ho.fx ? `1 USDT → ${ho.fx.base_rate} USDT` : '—',
      fxDraft: ho.fxDraft,
      fxError: ho.fxError,
      fxHistoryRows: (ho.fxHistory?.list ?? []).map((r) => ({ id: r.id, rate: r.rate ?? str(t, 'fxInherited'), at: r.effective_at, actor: r.actor_kind })),
      fxHasMore: !!ho.fxHistory && ho.fxHistory.page * ho.fxHistory.size < ho.fxHistory.total,
      fxNextPage: () => void loadMoreFXHistory(ctx),
      onFXRate: (e: InputEvent) => setHo(ctx, { fxDraft: e.target.value.slice(0, 32), fxError: '' }),
      saveFXRate: () => void saveFX(ctx),
      // X-40：结算时刻与时区取 config（02 §3），原型写死「每日 00:00」
      settleText: interpolate(str(t, 'settleDaily'), {
        settleTime: String(cfg?.commission['settle_time'] ?? '00:00'),
        timezone: cfg?.timezone ? tzOffsetLabel(cfg.timezone) : '',
      }),
    };
  },
};
